import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CommonIconsRegistry } from '@frontendlib/common/providers/icons/icons';
import { csrfInterceptor } from '@frontendlib/common/utils/csrf.interceptor';
import { FRONTEND_APP, FrontendApp } from '@frontendlib/common/providers/frontend-app.token';
import {
  provideGoogleAnalyticsPageTracking,
} from '@frontendlib/common/providers/google-analytics/provide-google-analytics';
import { provideIconsWithSprite } from '@frontendlib/common/providers/icons/provide-icons';
import { setEnvironment } from '@frontendlib/common/providers/environment';
import { ToastComponent } from '@frontendlib/common/features/toaster/components/toast.component';
import { ProductAnalyticsConsentComponent } from '@frontendlib/components/features/product-analytics-consent/product-analytics-consent.component';
import { unauthorizedInterceptor } from '@frontendlib/common/utils/unauthorized.interceptor';
import { adminStepUpInterceptor } from '@modules/common/interceptors/admin-step-up.interceptor';
import { HUGERTE_SCRIPT_SRC } from '@hugerte/hugerte-angular';
import { AccountModule } from '@modules/account/account.module';
import { provideMainProductAnalytics } from '@modules/common/providers/product-analytics.providers';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { createDisplayTimeZoneDateAdapter } from '@modules/full-calendar/types/display-timezone-date-adapter';
import { ModalService } from '@modules/modal/services/modal.service';
import { CalendarModule as AngularCalendarModule, DateAdapter } from 'angular-calendar';
import { provideMarkdown } from 'ngx-markdown';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { IconsRegistry } from './icons/icons';
import { ICON_SPRITE, ICON_SPRITE_NAMES } from './icons/icon-sprite.generated';
import { provideMainAppInitializer } from './main-app-initializer';
import { HeldStitchTrayComponent } from './modules/common/components/held-stitch-tray/held-stitch-tray.component';
import { provideErrorReporting } from './modules/error-reporting/provide-error-reporting';
import { ModalModule } from './modules/modal/modal.module';
import { NotificationModule } from './modules/notification/notification.module';
import { PagesModule } from './modules/pages/pages.module';
import { PopupModule } from './modules/popup/popup.module';
import { provideTopProgress } from './modules/top-progress/provide-top-progress';
import { TopProgressBarComponent } from './modules/top-progress/top-progress-bar.component';

setEnvironment(environment);

@NgModule({
  declarations: [AppComponent],
  imports: [
    AngularCalendarModule.forRoot({
      provide: DateAdapter,
      // SBUX-829: lay the calendar out in the app-wide display timezone (account → browser → UTC).
      useFactory: (displayTimeZoneService: DisplayTimeZoneService) =>
        createDisplayTimeZoneDateAdapter(() => displayTimeZoneService.timeZone()),
      deps: [DisplayTimeZoneService],
    }),
    AccountModule,
    AppRoutingModule,
    BrowserModule,
    FormsModule,
    HeldStitchTrayComponent,
    ModalModule,
    ProductAnalyticsConsentComponent,
    NotificationModule,
    PagesModule,
    PopupModule,
    TopProgressBarComponent,
  ],
  providers: [
    { provide: FRONTEND_APP, useValue: FrontendApp.MAIN },
    // HugeRTE is fetched when the first editor renders, not on every page load. The Angular wrapper
    // injects the script itself (once, shared) whenever no `hugerte` global is present; this is where
    // it gets the URL — without it, the wrapper loads from a public CDN. Relative, so it resolves
    // against the document base. HugeRTE then derives the theme, model, icons, skin and plugin URLs
    // from this script's own location — the app root, which is where `project.json` copies them.
    { provide: HUGERTE_SCRIPT_SRC, useValue: 'hugerte.min.js' },
    // The ErrorHandler (Sentry's, minus expected 401s), chunk-load recovery after a deploy, and the Sentry user.
    provideErrorReporting(),
    // Uncomment when enabling tracing (browserTracingIntegration + tracesSampleRate in main.ts)
    // { provide: Sentry.TraceService, deps: [Router] },
    // provideAppInitializer(() => {
    //   inject(Sentry.TraceService);
    // }),
    ModalService,
    // The admin step-up retries a write the gateway refused for want of the admin code (AdminStepUpGuard).
    provideHttpClient(withInterceptors([csrfInterceptor, unauthorizedInterceptor, adminStepUpInterceptor])),
    provideMainAppInitializer(),
    // The bar at the top of the page while a navigation or on-demand code loads (modules/top-progress/CLAUDE.md).
    provideTopProgress(),
    // The interface icons come in the bundle as one sprite (tools/icons/build-icon-sprite.mjs), not one file each.
    provideIconsWithSprite({ names: ICON_SPRITE_NAMES, svg: ICON_SPRITE }, CommonIconsRegistry, IconsRegistry),
    provideGoogleAnalyticsPageTracking(),
    provideMainProductAnalytics(),
    // Sanitize HTML in rendered markdown to prevent XSS from LLM-generated content
    // (e.g. <script>, <img onerror=...> embedded in knowledge hub responses)
    provideMarkdown(),
    provideAnimationsAsync(),
    provideToastr({
      preventDuplicates: false,
      positionClass: 'toast-bottom-right',
      tapToDismiss: false,
      toastComponent: ToastComponent,
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
